import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { useGoogleLogin, googleLogout } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import SEO from '../components/SEO';
import LoginImg from '../RapidMind 2025.png';
import '../index.css';
import SocialRegisterButtons from '../components/SocialRegisterButtons';

const Login = () => {
  const { login, logout } = useAuth();
  const navigate = useNavigate();

  // Google logout handler
  const handleGoogleLogout = () => {
    try {
      googleLogout();
      console.log('Google logout successful');
      // Also logout from your app's auth context
      logout();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error during Google logout:', error);
    }
  };

  // Google OAuth login configuration
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      console.log('Google login success:', tokenResponse);
      try {
        // Decode the JWT token to get user information
        let userInfo = null;
        if (tokenResponse.credential) {
          userInfo = jwtDecode(tokenResponse.credential);
          console.log('Decoded user info from JWT:', userInfo);
        }

        // Here you can handle the Google login response
        // For example, send the token to your backend or Firebase
        const response = await fetch('http://localhost:5000/api/auth/login-firebase', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            idToken: tokenResponse.access_token || tokenResponse.credential,
            uid: userInfo?.sub || tokenResponse.access_token, // Use sub from JWT or access_token
            email: userInfo?.email || 'user@example.com',
            displayName: userInfo?.name || 'Google User',
            emailVerified: userInfo?.email_verified || true,
            firstName: userInfo?.given_name || '',
            lastName: userInfo?.family_name || '',
            picture: userInfo?.picture || '',
          }),
        });

        if (response.ok) {
          const data = await response.json();
          console.log('Backend login success:', data);

          // If backend returns a token, you can decode it too
          if (data.token) {
            const decodedToken = jwtDecode(data.token);
            console.log('Decoded backend token:', decodedToken);
          }

          navigate('/profile', { replace: true });
        } else {
          const errorData = await response.json();
          console.error('Backend login failed:', errorData);
        }
      } catch (error) {
        console.error('Error during Google login process:', error);
      }
    },
    onError: (error) => {
      console.log('Google login error:', error);
    },
  });
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required field validation
    if (!formData.email?.trim()) newErrors.email = 'Email is required';
    if (!formData.password) newErrors.password = 'Password is required';
    
    // Email format validation
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    // Additional security check: prevent rapid form submissions
    if (loading) return;

    if (!validateForm()) return;

    setLoading(true);
    setErrors({}); // Clear previous errors

    try {
      console.log('🔐 [Login] Starting login process...');

      // Login without delay - optimized for speed
      const user = await login(formData.email, formData.password);

      console.log('🔐 [Login] Login successful, user authenticated:', user?.id);

      // Ensure user is set in context before navigation
      if (user) {
        console.log('🔐 [Login] User confirmed, navigating to profile...');
        navigate('/profile', { replace: true }); // Use replace to prevent back button issues
      } else {
        throw new Error('Login completed but user data not available');
      }
    } catch (error) {
      console.error('🔐 [Login] Login error:', error);

      // Handle specific error types
      if (error.message.includes('Please try again')) {
        setErrors({ submit: 'Please try again' });
      } else if (error.message.includes('Invalid credentials') || error.message.includes('User not found')) {
        setErrors({ submit: 'Invalid email or password' });
      } else if (error.message.includes('Server error')) {
        setErrors({ submit: 'Server error. Please try again.' });
      } else {
        setErrors({ submit: error.message });
      }
    }
    finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Login to RapidMind AI - Access Your AI Workspace"
        description="Sign in to your RapidMind AI account to access advanced AI tools, content creation features, and personalized workspace. Join thousands of creators using AI for innovation."
        keywords="login rapidmind ai, sign in AI tools, AI workspace login, content creation login, AI platform access, user authentication"
        image="/RapidMind 2025.png"
        type="website"
      />
      <div className="login-form-container">
        <h1>Login to Your Account</h1>
        <img id='login-icon' src={LoginImg} alt="Login" />
        <form onSubmit={handleSubmit}>
        <input 
          type="email" 
          name="email" 
          placeholder="Email" 
          value={formData.email}
          onChange={handleChange} 
        />
        {errors.email && <span className="error">{errors.email}</span>}

        <input 
          type="password" 
          name="password" 
          placeholder="Password" 
          value={formData.password}
          onChange={handleChange} 
        />
        {errors.password && <span className="error">{errors.password}</span>}

        {errors.submit && <span className="error submit-error">{errors.submit}</span>}

        <button type="submit" disabled={loading}>
          {loading ? 'Signing in...' : 'Sign In'}
        </button>
        <SocialRegisterButtons />
        <div className="login-links">
          <p>
            Don't have an account?{' '}
            <Link to="/Register" className="link">
              Sign up here
            </Link>
          </p>
          <p>
            <Link to="/forgot-password" className="link">
              Forgot your password?
            </Link>
          </p>
        </div>
      </form>
    </div>
    </>
  );
};

export default Login;
